import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add, People, Work, Delete, Refresh, Sync } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import JobStatusChip from '../components/JobStatusChip';
import PIJobSyncDialog from '../components/PIJobSyncDialog';
import { PIJobSyncResults } from '../services/api';

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  employment_type: string;
  status: string;
  posted_date: string;
  positions_available: number;
  positions_filled: number;
  total_candidates: number;
  candidate_stats: Record<string, number>;
  created_by?: string;
}

const Jobs: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  const canCreateJob = user?.role && ['admin', 'hr', 'manager'].includes(user.role);
  
  const canDeleteJob = (job: Job) => {
    if (!user?.role) return false;
    
    // Admin and HR can delete any job
    if (['admin', 'hr'].includes(user.role)) return true;
    
    // Managers can only delete jobs they created
    if (user.role === 'manager' && job.created_by === user.id) return true;
    
    return false;
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/jobs?status=all', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }
      
      const data = await response.json();
      setJobs(data.jobs || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (job: Job) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return;
    
    try {
      setDeleteLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/jobs/${jobToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle specific error cases
        if (response.status === 404) {
          // Job already deleted - remove from UI and refresh
          setJobs(jobs.filter(job => job.id !== jobToDelete.id));
          setSnackbar({
            open: true,
            message: `Job "${jobToDelete.title}" was already deleted. Refreshing list...`,
            severity: 'warning'
          });
          // Refresh the job list to get current state
          setTimeout(() => fetchJobs(), 1000);
          return;
        }
        
        throw new Error(errorData.details || errorData.error || 'Failed to delete job');
      }
      
      await response.json();
      
      // Remove job from local state
      setJobs(jobs.filter(job => job.id !== jobToDelete.id));
      setSnackbar({
        open: true,
        message: `Job "${jobToDelete.title}" deleted successfully`,
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Failed to delete job:', error);
      
      // Show specific error message from server if available
      const errorMessage = error instanceof Error && error.message
        ? (error.message.includes('Failed to delete job') 
            ? error.message 
            : `Failed to delete job: ${error.message}`)
        : 'Failed to delete job. Please try again.';
        
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      
      // Refresh job list in case of errors to sync with server state
      setTimeout(() => fetchJobs(), 2000);
      
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setJobToDelete(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSyncJobs = () => {
    setSyncDialogOpen(true);
  };

  const handleSyncComplete = (results: PIJobSyncResults) => {
    const jobParts = [];
    const { jobs, candidates } = results;

    if (jobs.linked > 0) jobParts.push(`${jobs.linked} linked`);
    if (jobs.created > 0) jobParts.push(`${jobs.created} created`);
    if (jobs.updated > 0) jobParts.push(`${jobs.updated} updated`);
    if (jobs.unlinked > 0) jobParts.push(`${jobs.unlinked} unlinked`);

    const messageParts = [];
    if (jobParts.length > 0) {
      messageParts.push(`Jobs: ${jobParts.join(', ')}`);
    }
    if (candidates.imported > 0) {
      messageParts.push(`Candidates: ${candidates.imported} imported`);
    }

    if (messageParts.length > 0) {
      setSnackbar({
        open: true,
        message: `Import complete! ${messageParts.join(' | ')}`,
        severity: 'success'
      });
      // Refresh the jobs list
      fetchJobs();
    } else {
      setSnackbar({
        open: true,
        message: 'No changes were made',
        severity: 'warning'
      });
    }

    if (jobs.errors.length > 0 || candidates.errors.length > 0) {
      console.error('Import errors:', { jobErrors: jobs.errors, candidateErrors: candidates.errors });
    }
  };

  const getEmploymentTypeLabel = (type: string) => {
    switch (type) {
      case 'full_time': return 'Full Time';
      case 'part_time': return 'Part Time';
      case 'contract': return 'Contract';
      case 'temporary': return 'Temporary';
      default: return type;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Group jobs by status
  const jobsByStatus = jobs.reduce((acc, job) => {
    const status = job.status || 'open';
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(job);
    return acc;
  }, {} as Record<string, Job[]>);

  // Define the order of statuses to display
  const statusOrder = ['open', 'paused', 'closed', 'filled'];
  const statusLabels: Record<string, string> = {
    open: 'Open Positions',
    paused: 'Paused Positions',
    closed: 'Closed Positions',
    filled: 'Filled Positions'
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Job Positions</Typography>
        <Box display="flex" gap={1}>
          <IconButton
            onClick={fetchJobs}
            disabled={loading}
            title="Refresh job list"
          >
            <Refresh />
          </IconButton>
          {canCreateJob && (
            <>
              <Button
                variant="outlined"
                startIcon={<Sync />}
                onClick={handleSyncJobs}
                data-testid="sync-jobs-button"
              >
                Import from PI
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/jobs/new')}
                data-testid="add-job-button"
              >
                Post New Job
              </Button>
            </>
          )}
        </Box>
      </Box>

      {jobs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Work sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No jobs posted yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Get started by posting your first job opening
          </Typography>
          {canCreateJob && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/jobs/new')}
              data-testid="add-job-button"
            >
              Post Your First Job
            </Button>
          )}
        </Paper>
      ) : (
        <Box>
          {statusOrder.map((status) => {
            const statusJobs = jobsByStatus[status];
            if (!statusJobs || statusJobs.length === 0) return null;
            
            return (
              <Box key={status} mb={4}>
                <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                  {statusLabels[status]} ({statusJobs.length})
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
                  {statusJobs.map((job) => (
            <Card key={job.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography 
                    variant="h6" 
                    component="h2" 
                    sx={{ 
                      flexGrow: 1, 
                      mr: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  >
                    {job.title}
                  </Typography>
                  <JobStatusChip
                    jobId={job.id}
                    currentStatus={job.status}
                    onStatusChange={(newStatus) => {
                      // Update the job in the local state
                      setJobs(prevJobs => 
                        prevJobs.map(j => 
                          j.id === job.id ? { ...j, status: newStatus } : j
                        )
                      );
                    }}
                    canEdit={canCreateJob}
                  />
                </Box>

                <Stack spacing={1} mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Department:</strong> {job.department}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location:</strong> {job.location}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Type:</strong> {getEmploymentTypeLabel(job.employment_type)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Positions:</strong> {job.positions_filled}/{job.positions_available}
                  </Typography>
                </Stack>

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <People fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {job.total_candidates || 0} candidates
                  </Typography>
                </Box>

                {job.candidate_stats && Object.keys(job.candidate_stats).length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {Object.entries(job.candidate_stats).map(([status, count]) => (
                      <Chip
                        key={status}
                        label={`${status}: ${count}`}
                        size="small"
                        variant="outlined"
                        clickable={false}
                      />
                    ))}
                  </Stack>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between' }}>
                <Button
                  size="small"
                  onClick={() => navigate(`/jobs/${job.id}`)}
                >
                  View Candidates
                </Button>
                {canDeleteJob(job) && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(job)}
                    title="Delete job"
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'error.main',
                        backgroundColor: 'error.light',
                        bgcolor: 'rgba(211, 47, 47, 0.04)'
                      }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </CardActions>
            </Card>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{jobToDelete?.title}"?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, color: 'text.secondary' }}>
            Department: {jobToDelete?.department} | {jobToDelete?.total_candidates || 0} candidates
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'medium', color: 'warning.main' }}>
            ⚠️ This will permanently delete the job and all associated candidates and evaluations. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete Job'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* PI Job Sync Dialog */}
      <PIJobSyncDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        onComplete={handleSyncComplete}
      />
    </Box>
  );
};

export default Jobs;